import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  TemplateRef,
} from '@angular/core';
import { BadgeComponent } from '../badge/badge.component';
import { TrimTooltipDirective } from '../../directives/trim-tooltip/trim-tooltip.directive';
import { StatColor, StatIconName } from './stat.model';
import { StatIconsComponent } from './stat-icons/stat-icons.component';
import { NgTemplateOutlet } from '@angular/common';
import { FeatureFlagsService } from '../../../core/common/services/feature-flags/feature-flags.service';
import { FEATURE_FLAGS } from '../../../core/common/constants/feature-flags.constants';
import { StatColorAllocatorService } from './stat-color-allocator.service';
import { ShortNumberPipe } from '../../pipes/short-number/short-number.pipe';

// A plain number, optionally with thousands commas and decimals (e.g. money-formatted values).
const NUMERIC_VALUE = /^-?[\d,]+(\.\d+)?$/;
const shortNumber = new ShortNumberPipe();

const STAT_DOT_CLASSES: Record<StatColor, string> = {
  destructive: 'bg-destructive-500',
  success: 'bg-success-500',
  warning: 'bg-warning-500',
  neutral: 'bg-neutral-900',
  primary: 'bg-primary-500',
};

@Component({
  selector: 'lib-stat',
  imports: [
    BadgeComponent,
    TrimTooltipDirective,
    StatIconsComponent,
    NgTemplateOutlet,
  ],
  templateUrl: './stat.component.html',
  styleUrl: './stat.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class StatComponent implements OnInit {
  private readonly featureFlags = inject(FeatureFlagsService);
  private readonly colorAllocator = inject(StatColorAllocatorService);
  private readonly destroyRef = inject(DestroyRef);

  label = input.required<string | number>();
  displayedLabel = computed(() => this.label().toString());
  overline = input<string>('');
  description = input<string>('');
  iconSvgName = input<StatIconName>('info');
  tag = input<number>(0);
  width = input<string>();
  // action icon is the icon that will be displayed when the tag is clicked
  actionIconTemplate = input<TemplateRef<unknown>>();
  actionIconClick = output<void>();
  BadgeLabel = input<string>();
  badgeClass = input<string>('');
  isLabelTrim = input<boolean>(false);
  trimLabelLength = input<number>(42);
  // fixed dot color for the new design; auto-assigned per page when omitted
  color = input<StatColor>();

  readonly isNewDesign = signal(
    this.featureFlags.getFeatureFlagValue(
      FEATURE_FLAGS.SYSTEM.insight_cards_new_design
    )
  );
  private readonly assignedColor = signal<StatColor>('neutral');
  readonly dotClass = computed(
    () => STAT_DOT_CLASSES[this.color() ?? this.assignedColor()]
  );
  // Only a deliberate destructive stat (e.g. overdue) tints its value; auto-assigned colors stay on the dot.
  readonly valueClass = computed(() =>
    this.color() === 'destructive' ? 'text-destructive-700' : 'text-neutral-900'
  );

  /** New design: big numbers read as 1.2K / 3.4M / 5B; text values are shown as given. */
  readonly displayedValue = computed(() => {
    const value = this.label().toString().trim();
    if (!NUMERIC_VALUE.test(value)) {
      return value;
    }
    return shortNumber.transform(Number(value.replace(/,/g, ''))).toUpperCase();
  });
  /** The full value, as a tooltip, when it was shortened. */
  readonly valueTitle = computed(() => {
    const full = this.label().toString();
    return this.displayedValue() === full.trim() ? null : full;
  });

  ngOnInit(): void {
    if (!this.isNewDesign() || this.color()) {
      return;
    }
    const color = this.colorAllocator.acquire();
    this.assignedColor.set(color);
    this.destroyRef.onDestroy(() => this.colorAllocator.release(color));
  }
}
